import { Controller } from "@hotwired/stimulus"

// Side drawer (slide-in from right). Used for inline creation flows where the
// parent page context should remain partially visible — preferable to a
// centered modal when the form is long (≥ 5 fields, multiple sections) or
// when the user benefits from glancing back at the parent form.
//
// Wiring (in the view loaded via turbo-frame):
//
//   <div role="dialog" aria-modal="true"
//        class="drawer-root"
//        data-controller="drawer"
//        data-action="click->drawer#close">
//     <div class="drawer-backdrop"></div>
//     <div class="drawer-panel" data-drawer-target="panel"
//          data-action="click->drawer#stopPropagation">
//       …
//     </div>
//   </div>
//
// Lifecycle :
//   - connect()    : freeze body scroll, animate panel in, focus first field,
//                    bind ESC + Tab focus-trap
//   - disconnect() : restore body scroll + previously focused element
//   - close()      : animate panel out, then clear the parent turbo-frame
//                    (which makes the drawer disappear from the DOM)
//   - stopPropagation() : on the inner panel, prevents backdrop click->close
//                         from firing when the user clicks inside the panel
//
// CSS contract (cf. app/assets/tailwind/components/drawer.css) :
//   .drawer-root           : fullscreen overlay, panel positioned right
//   .drawer-root.is-open   : panel slid into view, backdrop visible
//   .drawer-root.is-closing: panel sliding out, used during close animation
export default class extends Controller {
  static targets = ["panel"]
  static values = {
    closeAnimationMs: { type: Number, default: 220 },
    // Delay before placing focus on the first focusable element. Kept short
    // so keyboard users can start typing immediately. Configurable for
    // contexts where a longer entrance animation should finish first.
    focusDelayMs: { type: Number, default: 50 }
  }

  connect() {
    this.previouslyFocusedElement = document.activeElement
    this.previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    // Trigger the slide-in by adding `is-open` on the next paint. Without
    // this rAF, the browser collapses initial state (translateX(100%)) and
    // open state (translateX(0)) into a single layout pass — no animation.
    requestAnimationFrame(() => this.element.classList.add("is-open"))

    this.focusTimer = setTimeout(() => this.focusFirstElement(), this.focusDelayMsValue)

    this.boundKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundKeydown)
  }

  disconnect() {
    document.body.style.overflow = this.previousBodyOverflow
    document.removeEventListener("keydown", this.boundKeydown)
    if (this.focusTimer) clearTimeout(this.focusTimer)

    if (this.previouslyFocusedElement && document.contains(this.previouslyFocusedElement)) {
      this.previouslyFocusedElement.focus()
    }
  }

  close() {
    if (this.element.classList.contains("is-closing")) return  // already closing

    this.element.classList.add("is-closing")
    this.element.classList.remove("is-open")

    setTimeout(() => {
      const frame = this.element.closest("turbo-frame")
      if (frame) {
        // Clear the frame by removing children — safer than innerHTML="" per
        // the project's security hook (no parsed-HTML side effects).
        while (frame.firstChild) frame.removeChild(frame.firstChild)
      } else {
        this.element.remove()
      }
    }, this.closeAnimationMsValue)
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
    } else if (event.key === "Tab") {
      this.handleTab(event)
    }
  }

  handleTab(event) {
    const focusables = this.focusableElements()
    if (focusables.length === 0) return

    const first = focusables[0]
    const last = focusables[focusables.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  focusableElements() {
    const selector = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled]):not([type='hidden'])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "[tabindex]:not([tabindex='-1'])"
    ].join(", ")
    return Array.from(this.element.querySelectorAll(selector))
      .filter(el => el.offsetParent !== null)  // skip hidden via CSS
  }

  focusFirstElement() {
    const focusables = this.focusableElements()
    if (focusables.length > 0) focusables[0].focus()
  }
}
